import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpeg = null;

// Initialize FFmpeg instance
const initFFmpeg = async () => {
  if (!ffmpeg) {
    ffmpeg = new FFmpeg();
    
    // Load FFmpeg with proper core URLs
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
  }
  
  return ffmpeg;
};

// Check if file needs conversion
export const needsConversion = (file) => {
  const fileName = file.name.toLowerCase();
  const supportedFormats = ['.wav', '.flac', '.mp3'];
 
  
  return !supportedFormats.some(format => fileName.endsWith(format));
};

// Convert audio file to MP3
export const convertToMp3 = async (file, onProgress = null) => {
  try {
    // Initialize FFmpeg
    const ffmpegInstance = await initFFmpeg();
    
    // Generate unique filenames
    const inputName = `input_${Date.now()}.${file.name.split('.').pop()}`;
    const outputName = `output_${Date.now()}.mp3`;
 const supportedFormats = ['.wav', '.flac', '.mp3', '.webm'];
   
    // Write input file to FFmpeg virtual filesystem
    await ffmpegInstance.writeFile(inputName, await fetchFile(file));
    
    // Progress callback
    if (onProgress) {
      ffmpegInstance.on('progress', ({ progress }) => {
        onProgress(Math.round(progress * 100));
      });
    }
    
    // Convert to MP3 with good quality settings
    await ffmpegInstance.exec([
      '-i', inputName,
      '-codec:a', 'libmp3lame',
      '-b:a', '192k',
      '-ar', '44100',
      outputName
    ]);
    
    // Read the output file
    const data = await ffmpegInstance.readFile(outputName);
    
    // Clean up files from virtual filesystem
    await ffmpegInstance.deleteFile(inputName);
    await ffmpegInstance.deleteFile(outputName);
    
    // Create new File object with MP3 data
    const mp3Blob = new Blob([data], { type: 'audio/mp3' });
    const originalName = file.name.replace(/\.[^/.]+$/, '');
    const mp3File = new File([mp3Blob], `${originalName}.mp3`, {
      type: 'audio/mp3',
      lastModified: Date.now()
    });
    
    return mp3File;
    
  } catch (error) {
    console.error('Audio conversion failed:', error);
    throw new Error(`Failed to convert audio file: ${error.message}`);
  }
};

// Convert video file to audio (MP3)
export const convertVideoToAudio = async (file, onProgress = null) => {
  try {
    // Initialize FFmpeg
    const ffmpegInstance = await initFFmpeg();

    // Generate unique filenames
    const inputName = `input_video_${Date.now()}.${file.name.split('.').pop()}`;
    const outputName = `output_audio_${Date.now()}.mp3`;

    // Write input file to FFmpeg virtual filesystem
    await ffmpegInstance.writeFile(inputName, await fetchFile(file));

    // Progress callback
    if (onProgress) {
      ffmpegInstance.on('progress', ({ progress }) => {
        onProgress(Math.round(progress * 100));
      });
    }

    // Extract audio from video and convert to MP3
    await ffmpegInstance.exec([
      '-i', inputName,
      '-vn', // No video
      '-codec:a', 'libmp3lame',
      '-b:a', '192k',
      '-ar', '44100',
      outputName
    ]);

    // Read the output file
    const data = await ffmpegInstance.readFile(outputName);

    // Clean up files from virtual filesystem
    await ffmpegInstance.deleteFile(inputName);
    await ffmpegInstance.deleteFile(outputName);

    // Create new File object with MP3 data
    const mp3Blob = new Blob([data], { type: 'audio/mp3' });
    const originalName = file.name.replace(/\.[^/.]+$/, '');
    const mp3File = new File([mp3Blob], `${originalName}.mp3`, {
      type: 'audio/mp3',
      lastModified: Date.now()
    });

    return mp3File;

  } catch (error) {
    console.error('Video to audio conversion failed:', error);
    const errorMessage = error?.message || error?.toString() || 'Unknown error during conversion';
    throw new Error(`Failed to extract audio from video: ${errorMessage}`);
  }
};

// Get file format info
export const getFileFormat = (file) => {
  const extension = file.name.toLowerCase().split('.').pop();
  const formatMap = {
    'wav': 'WAV',
    'flac': 'FLAC',
    'mp3': 'MP3',
    'ogg': 'OGG',
    'm4a': 'M4A',
    'aac': 'AAC',
    'wma': 'WMA',
    'opus': 'OPUS'
  };

  return formatMap[extension] || extension.toUpperCase();
};
